/**
 * MaturityChartOverlay (#1184 v2.569.0) — markiert in Monats-Trend-Charts
 * den unsicheren Bereich (Vormonat + aktueller Monat) als gelbe ReferenceArea
 * mit Label „vorläufig". Gilt für ALLE Investor-Segment-Pages + Wasserfall.
 *
 * Verwendung:
 *   <LineChart data={chartData}>
 *     ...
 *     {MaturityOverlay({ year, monthLabels })}
 *   </LineChart>
 *
 * Funktioniert mit LineChart / BarChart / ComposedChart (Recharts).
 */
import { ReferenceArea } from 'recharts';
import { todayVienna, lastMatureMonth, MONTH_LABELS_DE } from '../../../utils/data-maturity';

/**
 * Returns array of Recharts `ReferenceArea` elements (0..1) for the tentative
 * months of a given year. Empty array when the whole year is mature.
 *
 * @param {{year: number, monthLabels: string[], today?: Date}} props
 * @returns Recharts elements (must be unfolded inside the chart via {...})
 */
export function MaturityOverlay({ year, monthLabels, today }) {
  const t = today || new Date();
  const v = todayVienna(t);
  if (year < v.year) return null; // ganzes Jahr belastbar
  if (year > v.year) return null; // ganzes Jahr Zukunft (kein Datum)
  const cutoff = lastMatureMonth(year, t);
  // Erster vorläufiger Monat = cutoff+1 (oder 1 wenn kein cutoff)
  const firstTentativeIdx = cutoff !== null ? cutoff : 0; // 0-based array index → cutoff (which is 1-based, so cutoff = index of first tentative)
  if (firstTentativeIdx >= monthLabels.length) return null;
  const x1 = monthLabels[firstTentativeIdx];
  const x2 = monthLabels[monthLabels.length - 1];
  // Recharts braucht string-keys, identisch zur XAxis dataKey.
  return (
    <ReferenceArea
      x1={x1}
      x2={x2}
      y1={undefined}
      y2={undefined}
      strokeOpacity={0}
      fill="#FEF3C7"
      fillOpacity={0.45}
      ifOverflow="extendDomain"
      label={{
        value: 'vorläufig / unvollständig',
        position: 'insideTopRight',
        fill: '#92400E',
        fontSize: 10,
      }}
    />
  );
}

/** Plain string-label „belastbar bis inkl. März 2026". Für Footer-Hinweise. */
export function reifeFooter(year, today) {
  const t = today || new Date();
  const v = todayVienna(t);
  if (year < v.year) return `${year} vollständig belastbar`;
  if (year > v.year) return null;
  const cutoff = lastMatureMonth(year, t);
  if (cutoff === null) return `${year} hat noch keine belastbaren Monate (mind. 2 abgeschlossene Monate erforderlich)`;
  return `Belastbar bis inkl. ${MONTH_LABELS_DE[cutoff - 1]} ${year} · Vormonat vorläufig · Aktueller Monat unvollständig (gelb markiert)`;
}
